import React, { useState, useCallback, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { useDropzone } from 'react-dropzone';
import {
	Box,
	Button,
	Image,
	Input,
	Text,
	Heading,
	useToast,
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalFooter,
	ModalBody,
	ModalCloseButton,
	useDisclosure,
	VStack,
	HStack,
	Divider,
} from '@chakra-ui/react';
import uploadIcon from '../../../assets/upload_icon.png';
import AutoTypeText from '../multistepform/autotype';
import { useSelector } from 'react-redux';
import { saveAs } from 'file-saver';
import VendorInvoicePaginatedData from './vendorInvoicePaginatedData';
import { useAdvances } from '../../../Context/AdvancesContext';
import { Spinner } from "@chakra-ui/react";


const VendorInvoice = ({ onAdvanceClick }) => {
	const toast = useToast();
	const [data, setData] = useState([]);
	const [editing, setEditing] = useState(false);
	const companyId = useSelector((state) => state.companyId);
	const selectedLocation = useSelector((state) => state.selectedLocation);
	const selectedBranch = useSelector((state) => state.selectedBranch);
	const locationCode = useSelector((state) => state.locationCode);
	const [errors, setErrors] = useState([]);
	const [hasValidationErrors, setHasValidationErrors] = useState(false);
	const [uploadMessageExcel, setUploadMessageExcel] = useState(undefined);
	const [errorValue, setErrorValue] = useState();
	const [totalOpenBalance, setTotalOpenBalance] = useState(null);
	const [customerList, setCustomerList] = useState({ message: [], parentGlValue: 0 });
	const [pagesWithErrors, setPagesWithErrors] = useState(new Set());
	const [currentPage, setCurrentPage] = useState(1);
	const itemsPerPage = 50;
	const { isOpen, onOpen, onClose } = useDisclosure();
	const [customerLimits, setCustomerLimits] = useState([]);
	const [violations, setViolations] = useState([]);
	const [loading, setLoading] = useState(false);
	const [AdvanceLimitsVendor, setAdvanceLimitsVendor] = useState([]);
	// Use context
	const { setAdvancesvendor, setPositiveMissingCustomers } = useAdvances();


	// function bigPrint(number) {
	// 	if (number == null || isNaN(number)) return 0;
	// 	return +number.toString();
	// }
	function bigPrint(number) {
		if (number == null || isNaN(number)) return "0.00";
		return Number(number).toFixed(2); // always 2 decimals
	}

	function formatDateToDdMmYy(date) {
		const day = String(date.getDate()).padStart(2, '0');
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const year = String(date.getFullYear()).slice(-2);
		return `${day}-${month}-${year}`;
	}

	function convertExcelDate(value) {
		if (!value) return '';

		let serial;
		if (value instanceof Date) {
			serial = Math.floor((value.getTime() / 86400000) + 25569);
		} else if (typeof value === 'number' && value > 1 && value < 100000) {
			serial = Math.floor(value);
		} else {
			return String(value);
		}
		const date = new Date((serial - 2) * 86400000);

		if (isNaN(date.getTime())) return String(value);
		const day = String(date.getDate()).padStart(2, '0');
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const year = date.getFullYear();
		return `${day}-${month}-${year}`;
	}

	const currentDate = new Date();
	const formattedDate = formatDateToDdMmYy(currentDate);

	useEffect(() => {
		const newErrorValue = errors.reduce((acc, rowErrors) => {
			return acc + Object.values(rowErrors).filter((error) => error !== null).length;
		}, 0);
		setErrorValue(newErrorValue);
	}, [errors]);

	// 1st API: Customer List
	useEffect(() => {
		const glData = async () => {
			try {
				setLoading(true);
				const response = await fetch(
					`${process.env.REACT_APP_API_URL}vendor-list.php?company_id=${companyId}&branch_id=${selectedBranch}&location_id=${selectedLocation}`,
					{ method: 'GET' }
				);
				if (response.ok) {
					const data = await response.json();
					setCustomerList(data);
				}
			} catch {
				console.log('error');
			} finally {
				setLoading(false);
			}
		};
		glData();
	}, [companyId, selectedBranch, selectedLocation]);

	// 2nd API: Customer Limits
	useEffect(() => {
		const fetchCustomerLimits = async () => {
			try {
				const formData = new FormData();
				formData.append('company_id', companyId);
				formData.append('location_id', selectedLocation);
				formData.append('branch_id', selectedBranch);
				formData.append('type', 'vendor');

				const response = await fetch(`${process.env.REACT_APP_API_URL}api-customer-value.php`, {
					method: 'POST',
					body: formData,
				});

				if (response.ok) {
					const result = await response.json();
					setCustomerLimits(result.data || []);

				}

			} catch (error) {
				console.error('Failed to fetch customer limits:', error);
			}
		};

		fetchCustomerLimits();
	}, [companyId, selectedLocation, selectedBranch]);

	// 3nd API: Advance Limits
	useEffect(() => {
		const checkAdvanceLimitsVendor = async () => {
			try {
				setLoading(true);
				const response = await fetch(
					`${process.env.REACT_APP_API_URL}advance-list.php?company_id=${companyId}&branch_id=${selectedBranch}&location_id=${selectedLocation}&type=vendor`,
					{ method: 'GET' }
				);
				if (response.ok) {
					const data = await response.json();
					setAdvanceLimitsVendor(data);
				}
			} catch {
				console.log('error');
			} finally {
				setLoading(false);
			}
		};
		checkAdvanceLimitsVendor();
	}, [companyId, selectedBranch, selectedLocation]);
	console.log("AdvanceLimitsCustomerVendor 🟢🔵", AdvanceLimitsVendor);

	// File drop
	const handleFileDrop = useCallback((acceptedFiles) => {
		const file = acceptedFiles[0];
		const reader = new FileReader();

		reader.onload = (event) => {
			const dataArray = new Uint8Array(event.target.result);
			const workbook = XLSX.read(dataArray, { type: 'array', raw: true });

			const sheetName = workbook.SheetNames[0];
			const worksheet = workbook.Sheets[sheetName];

			const excelData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
			if (!excelData || excelData.length === 0) {
				setData([]);
				return;
			}

			const headers = excelData[0].map((h) => (typeof h === 'string' ? h.trim() : String(h)));
			const formattedData = excelData.slice(1).map((row) => {
				const rowData = {};
				row.forEach((cell, columnIndex) => {
					const header = headers[columnIndex] ?? `col_${columnIndex}`;
					rowData[header] = cell;
				});
				return rowData;
			});

			const emptyRow = Object.fromEntries(headers.map((header) => [header, '']));
			const dataWithEmptyKeys = formattedData.map((row) => ({ ...emptyRow, ...row }));

			// const nonBlankRows = dataWithEmptyKeys.filter((row) =>
			// 	Object.values(row).some((cellValue) => cellValue !== null && cellValue !== '')
			// );
			const nonBlankRows = dataWithEmptyKeys.map((row) => {
				const formattedRow = { ...row };
				if (row.inv_date !== undefined) {
					formattedRow.inv_date = convertExcelDate(row.inv_date);
					formattedRow.inv_posting_date = convertExcelDate(row.inv_posting_date);
					formattedRow.inv_due_date = convertExcelDate(row.inv_due_date);
					formattedRow.inv_no = String(row.inv_no);
				}

				return formattedRow;
			}).filter((row) =>
				Object.values(row).some((cellValue) => cellValue !== null && cellValue !== '')
			);
			setData(nonBlankRows);
			setUploadMessageExcel(undefined);
		};

		reader.readAsArrayBuffer(file);
	}, []);


	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop: handleFileDrop,
		accept: '.xlsx',
	});



	// Helper functions for violation check
	// const checkViolations = useCallback((dataToCheck) => {
	// 	if (!dataToCheck || dataToCheck.length === 0 || !customerLimits || customerLimits.length === 0) {
	// 		setViolations([]);
	// 		setAdvancesvendor([]);
	// 		return;
	// 	}

	// 	const newViolations = [];
	// 	const fileCodes = new Set();
	// 	const customerTotals = {};

	// 	// Step 1: Aggregate file rows
	// 	for (let i = 0; i < dataToCheck.length; i++) {
	// 		const row = dataToCheck[i];
	// 		const rawCode =
	// 			getRowValue(row, ['vendorcode', 'vendor_code', 'vendor', 'subgl', 'sub_gl', 'custcode']) ??
	// 			row.vendor_code ??
	// 			row.subgl ??
	// 			row['Vendor Code'] ??
	// 			row['SubGL'] ??
	// 			'';

	// 		const code = rawCode != null ? String(rawCode).trim() : '';
	// 		if (!code) continue;

	// 		fileCodes.add(code);

	// 		const rawDue =
	// 			getRowValue(row, ['dueamt', 'due_amount', 'dueamount', 'due_amt', 'amountdue', 'totalamt', 'due']) ??
	// 			row.due_amt ??
	// 			row['Due Amount'] ??
	// 			row['due'];

	// 		let dueAmt = 0;
	// 		if (!isBlankValue(rawDue)) {
	// 			dueAmt = parseNumber(rawDue);
	// 			if (isNaN(dueAmt)) dueAmt = 0;
	// 		}

	// 		if (!customerTotals[code]) {
	// 			customerTotals[code] = { totalDue: 0, rows: [] };
	// 		}

	// 		customerTotals[code].totalDue += dueAmt;
	// 		customerTotals[code].rows.push({ rowIndex: i + 1, dueAmt });
	// 	}

	// 	// Step 2: Check against vendor limits
	// 	Object.entries(customerTotals).forEach(([code, { totalDue, rows }]) => {
	// 		const limitObj = customerLimits.find((c) => {
	// 			const candidates = [c.subgl, c.gl_code, c.gl, c.gl_label, c.vendor_code];
	// 			return candidates.some((field) => field != null && String(field).trim() === code);
	// 		});

	// 		if (!limitObj) {
	// 			newViolations.push({
	// 				rowIndex: rows.map(r => r.rowIndex).join(", "),
	// 				code,
	// 				dueAmt: totalDue,
	// 				limitValue: null,
	// 				advance: null,
	// 				message: "Vendor Code not found",
	// 			});
	// 			return;
	// 		}

	// 		const limitValue = parseNumber(limitObj.from_opening_value);
	// 		if (isNaN(limitValue)) return;

	// 		let isViolation = false;
	// 		if (limitValue >= 0) {
	// 			if (totalDue > limitValue) isViolation = true;
	// 		} else {
	// 			if (totalDue >= 0) {
	// 				isViolation = true;
	// 			} else if (Math.abs(totalDue) > Math.abs(limitValue)) {
	// 				isViolation = true;
	// 			}
	// 		}

	// 		if (isViolation) {
	// 			const advance = totalDue - limitValue;
	// 			newViolations.push({
	// 				rowIndex: rows.map(r => r.rowIndex).join(", "),
	// 				code,
	// 				dueAmt: totalDue,
	// 				limitValue,
	// 				advance,
	// 				message: "Limit Exceeded",
	// 			});
	// 		}
	// 		//new 
	// 		if (totalDue === 0) {
	// 			newViolations.push({
	// 				rowIndex: rows.map(r => r.rowIndex).join(", "),
	// 				code,
	// 				dueAmt: 0,
	// 				limitValue,
	// 				advance: Math.abs(limitValue),
	// 				message: "Advance Customer (Zero Due → Allowed Limit Considered)",
	// 			});
	// 		}
	// 	});

	// 	// Step 3: Negative vendors not in file
	// 	customerLimits.forEach((c) => {
	// 		const code = String(c.vendor_code ?? c.subgl ?? "").trim();
	// 		if (!code) return;

	// 		const limitValue = parseNumber(c.from_opening_value);
	// 		if (isNaN(limitValue)) return;

	// 		if (limitValue < 0 && !fileCodes.has(code)) {
	// 			newViolations.push({
	// 				rowIndex: null,
	// 				code,
	// 				dueAmt: 0,
	// 				limitValue,
	// 				advance: Math.abs(limitValue),
	// 				message: "Advance Vendor (Not in File)",
	// 			});
	// 		}

	// 		if (limitValue < 0 && fileCodes.has(code) && customerTotals[code]?.totalDue === 0) {
	// 			newViolations.push({
	// 				rowIndex: customerTotals[code].rows.map(r => r.rowIndex).join(", "),
	// 				code,
	// 				dueAmt: 0,
	// 				limitValue,
	// 				advance: Math.abs(limitValue),
	// 				message: "Advance Vendor (Zero Due)",
	// 			});
	// 		}
	// 	});

	// 	// Step 4: Build advances array
	// 	const advancesArray = newViolations
	// 		.filter(v => v.advance !== null && v.advance !== undefined)
	// 		.map(v => ({
	// 			code: v.code,
	// 			advance: v.advance
	// 		}));
	// 	console.log("advancesArrayVendor🔵", advancesArray);

	// 	// Step 5: Normalize AdvanceLimitsVendor
	// 	const advanceList = Array.isArray(AdvanceLimitsVendor)
	// 		? AdvanceLimitsVendor
	// 		: AdvanceLimitsVendor?.data || [];
	// 	console.log("advanceListVendor (normalized)", advanceList);

	// 	// helper: compare numbers with tolerance
	// 	const numbersMatch = (a, b) => {
	// 		if (a == null || b == null) return false;
	// 		return Math.abs(parseNumber(a) - parseNumber(b)) < 0.01;
	// 	};

	// 	// Step 6: Filter out violations already satisfied by AdvanceLimitsVendor
	// 	const filteredViolations = newViolations.filter(v => {
	// 		if (!v.code || v.advance == null) return true;
	// 		const match = advanceList.find(a =>
	// 			String(a.vendor_code ?? a.customer_code).trim() === String(v.code).trim() &&
	// 			numbersMatch(a.payment_amt, v.advance)
	// 		);
	// 		return !match;
	// 	});

	// 	setViolations(filteredViolations);

	// 	// Step 7: Keep only advances not satisfied
	// 	const advancesArrayFiltered = advancesArray.filter(a => {
	// 		const match = advanceList.find(api =>
	// 			String(api.vendor_code ?? api.customer_code).trim() === String(a.code).trim() &&
	// 			numbersMatch(api.payment_amt, a.advance)
	// 		);
	// 		return !match;
	// 	});
	// 	setAdvancesvendor(advancesArrayFiltered);

	// 	// Step 8: Open modal only if still violations left
	// 	if (filteredViolations.length > 0) {
	// 		onOpen();
	// 	}
	// }, [customerLimits, AdvanceLimitsVendor, onOpen, setAdvancesvendor]);
	// helper for rounding to 2 decimals
	// helper for rounding to 2 decimals
	// helper for rounding to 2 decimals
	// helper for rounding to 2 decimals
	// function round2(num) {
	// 	if (num == null || isNaN(num)) return 0;
	// 	return Math.round((num + Number.EPSILON) * 100) / 100;
	// }

	// const checkViolations = useCallback((dataToCheck) => {
	// 	if (!dataToCheck || dataToCheck.length === 0 || !customerLimits || customerLimits.length === 0) {
	// 		setViolations([]);
	// 		setAdvancesvendor([]);
	// 		return;
	// 	}

	// 	const newViolations = [];
	// 	const fileCodes = new Set();
	// 	const customerTotals = {};

	// 	// Step 1: Aggregate file rows
	// 	for (let i = 0; i < dataToCheck.length; i++) {
	// 		const row = dataToCheck[i];
	// 		const rawCode =
	// 			getRowValue(row, ['vendorcode', 'vendor_code', 'vendor', 'subgl', 'sub_gl', 'custcode']) ??
	// 			row.vendor_code ??
	// 			row.subgl ??
	// 			row['Vendor Code'] ??
	// 			row['SubGL'] ??
	// 			'';

	// 		const code = rawCode != null ? String(rawCode).trim() : '';
	// 		if (!code) continue;

	// 		fileCodes.add(code);

	// 		const rawDue =
	// 			getRowValue(row, ['dueamt', 'due_amount', 'dueamount', 'due_amt', 'amountdue', 'totalamt', 'due']) ??
	// 			row.due_amt ??
	// 			row['Due Amount'] ??
	// 			row['due'];

	// 		let dueAmt = 0;
	// 		if (!isBlankValue(rawDue)) {
	// 			dueAmt = parseNumber(rawDue);
	// 			if (isNaN(dueAmt)) dueAmt = 0;
	// 		}

	// 		if (!customerTotals[code]) {
	// 			customerTotals[code] = { totalDue: 0, rows: [] };
	// 		}

	// 		customerTotals[code].totalDue = round2(customerTotals[code].totalDue + dueAmt);
	// 		customerTotals[code].rows.push({ rowIndex: i + 1, dueAmt: round2(dueAmt) });
	// 	}

	// 	// Step 2: Check against vendor limits
	// 	Object.entries(customerTotals).forEach(([code, { totalDue, rows }]) => {
	// 		const limitObj = customerLimits.find((c) => {
	// 			const candidates = [c.subgl, c.gl_code, c.gl, c.gl_label, c.vendor_code];
	// 			return candidates.some((field) => field != null && String(field).trim() === code);
	// 		});

	// 		if (!limitObj) {
	// 			newViolations.push({
	// 				rowIndex: rows.map(r => r.rowIndex).join(", "),
	// 				code,
	// 				dueAmt: round2(totalDue),
	// 				limitValue: null,
	// 				advance: null,
	// 				message: "Vendor Code not found",
	// 			});
	// 			return;
	// 		}

	// 		const limitValue = round2(parseNumber(limitObj.from_opening_value));
	// 		if (isNaN(limitValue)) return;

	// 		let isViolation = false;
	// 		if (limitValue >= 0) {
	// 			if (totalDue > limitValue) isViolation = true;
	// 		} else {
	// 			if (totalDue >= 0) {
	// 				isViolation = true;
	// 			} else if (Math.abs(totalDue) > Math.abs(limitValue)) {
	// 				isViolation = true;
	// 			}
	// 		}

	// 		if (isViolation) {
	// 			newViolations.push({
	// 				rowIndex: rows.map(r => r.rowIndex).join(", "),
	// 				code,
	// 				dueAmt: round2(totalDue),
	// 				limitValue: round2(limitValue),
	// 				advance: round2(totalDue - limitValue),
	// 				message: "Limit Exceeded",
	// 			});
	// 		}

	// 		// new logic: zero due → treat limit as advance
	// 		if (totalDue === 0) {
	// 			newViolations.push({
	// 				rowIndex: rows.map(r => r.rowIndex).join(", "),
	// 				code,
	// 				dueAmt: 0,
	// 				limitValue: round2(limitValue),
	// 				advance: Math.abs(round2(limitValue)),
	// 				message: "Advance Customer (Zero Due → Allowed Limit Considered)",
	// 			});
	// 		}
	// 	});

	// 	// Step 3: Negative vendors not in file
	// 	customerLimits.forEach((c) => {
	// 		const code = String(c.vendor_code ?? c.subgl ?? "").trim();
	// 		if (!code) return;

	// 		const limitValue = round2(parseNumber(c.from_opening_value));
	// 		if (isNaN(limitValue)) return;

	// 		if (limitValue < 0 && !fileCodes.has(code)) {
	// 			newViolations.push({
	// 				rowIndex: null,
	// 				code,
	// 				dueAmt: 0,
	// 				limitValue: round2(limitValue),
	// 				advance: Math.abs(round2(limitValue)),
	// 				message: "Advance Vendor (Not in File)",
	// 			});
	// 		}

	// 		if (limitValue < 0 && fileCodes.has(code) && customerTotals[code]?.totalDue === 0) {
	// 			newViolations.push({
	// 				rowIndex: customerTotals[code].rows.map(r => r.rowIndex).join(", "),
	// 				code,
	// 				dueAmt: 0,
	// 				limitValue: round2(limitValue),
	// 				advance: Math.abs(round2(limitValue)),
	// 				message: "Advance Vendor (Zero Due)",
	// 			});
	// 		}
	// 	});

	// 	// Step 4: Add advance entries for vendors in customerLimits but not in file
	// 	customerLimits.forEach((c) => {
	// 		const code = String(c.vendor_code ?? c.subgl ?? "").trim();
	// 		if (!code) return;

	// 		const limitValue = round2(parseNumber(c.from_opening_value));
	// 		if (isNaN(limitValue)) return;

	// 		if (!fileCodes.has(code)) {
	// 			const advanceVal = Math.abs(limitValue);

	// 			newViolations.push({
	// 				rowIndex: null,
	// 				code,
	// 				dueAmt: 0,
	// 				limitValue: round2(limitValue),
	// 				advance: advanceVal,
	// 				message: "Advance Vendor (Missing in File → Allowed Limit Considered)",
	// 			});
	// 		}
	// 	});

	// 	// Step 5: Build advances array AFTER all pushes
	// 	let advancesArray = newViolations
	// 		.filter(v => v.advance !== null && v.advance !== undefined)
	// 		.map(v => ({
	// 			code: v.code,
	// 			advance: round2(v.advance),
	// 		}));
	// 	console.log("advancesArrayVendor🔵", advancesArray);

	// 	// Step 6: Normalize AdvanceLimitsVendor
	// 	const advanceList = Array.isArray(AdvanceLimitsVendor)
	// 		? AdvanceLimitsVendor
	// 		: AdvanceLimitsVendor?.data || [];
	// 	console.log("advanceListVendor (normalized)", advanceList);

	// 	// helper: compare numbers with tolerance
	// 	const numbersMatch = (a, b) => {
	// 		if (a == null || b == null) return false;
	// 		return Math.abs(round2(parseNumber(a)) - round2(parseNumber(b))) < 0.01;
	// 	};

	// 	// Step 7: Filter out advances already satisfied by AdvanceLimitsVendor
	// 	advancesArray = advancesArray.filter(a => {
	// 		const match = advanceList.find(api =>
	// 			String(api.vendor_code ?? api.customer_code).trim() === String(a.code).trim() &&
	// 			numbersMatch(api.payment_amt, a.advance)
	// 		);
	// 		return !match;
	// 	});

	// 	// Step 8: Update state
	// 	setViolations(newViolations);
	// 	setAdvancesvendor(advancesArray);

	// 	// Step 9: Open modal only if still violations left
	// 	if (newViolations.length > 0) {
	// 		onOpen();
	// 	}
	// }, [customerLimits, AdvanceLimitsVendor, onOpen, setAdvancesvendor]);

	// helper for rounding to 2 decimals
	function round2(num) {
		if (num == null || isNaN(num)) return 0;
		return Math.round((num + Number.EPSILON) * 100) / 100;
	}

	const checkViolations = useCallback((dataToCheck) => {
		if (!dataToCheck || dataToCheck.length === 0 || !customerLimits || customerLimits.length === 0) {
			setViolations([]);
			setAdvancesvendor([]);
			return;
		}

		const newViolations = [];
		const fileCodes = new Set();
		const customerTotals = {};

		// Step 1: Collect totals per vendor
		for (let i = 0; i < dataToCheck.length; i++) {
			const row = dataToCheck[i];
			const rawCode =
				getRowValue(row, ['vendorcode', 'vendor_code', 'vendor', 'subgl', 'sub_gl', 'custcode']) ??
				row.vendor_code ??
				row.subgl ??
				row['Vendor Code'] ??
				row['SubGL'] ??
				'';

			const code = rawCode != null ? String(rawCode).trim() : '';
			if (!code) continue;

			fileCodes.add(code);

			const rawDue =
				getRowValue(row, ['dueamt', 'due_amount', 'dueamount', 'due_amt', 'amountdue', 'totalamt', 'due']) ??
				row.due_amt ??
				row['Due Amount'] ??
				row['due'];

			let dueAmt = 0;
			if (!isBlankValue(rawDue)) {
				dueAmt = parseNumber(rawDue);
				if (isNaN(dueAmt)) dueAmt = 0;
			}

			if (!customerTotals[code]) {
				customerTotals[code] = { totalDue: 0, rows: [] };
			}

			customerTotals[code].totalDue = round2(customerTotals[code].totalDue + dueAmt);
			customerTotals[code].rows.push({ rowIndex: i + 1, dueAmt: round2(dueAmt) });
		}

		// Step 2: Check against limits
		Object.entries(customerTotals).forEach(([code, { totalDue, rows }]) => {
			const limitObj = customerLimits.find((c) => {
				const candidates = [c.subgl, c.gl_code, c.gl, c.gl_label, c.vendor_code];
				return candidates.some((field) => field != null && String(field).trim() === code);
			});

			if (!limitObj) {
				newViolations.push({
					rowIndex: rows.map(r => r.rowIndex).join(", "),
					code,
					dueAmt: round2(totalDue),
					limitValue: null,
					advance: null,
					message: "Vendor Code not found",
				});
				return;
			}

			const limitValue = round2(parseNumber(limitObj.from_opening_value));
			if (isNaN(limitValue)) return;

			// --- Advance Logic: only if limitValue > totalDue ---
			let advance = 0;
			if (limitValue > totalDue) {
				advance = round2(limitValue - totalDue);
			}
			// --- Violation Logic ---
			// if (totalDue > limitValue) {
			// 	newViolations.push({
			// 		rowIndex: rows.map(r => r.rowIndex).join(", "),
			// 		code,
			// 		dueAmt: round2(totalDue),
			// 		limitValue: round2(limitValue),
			// 		advance: null,
			// 		message: "Limit Exceeded",
			// 	});
			// }
			if (totalDue > limitValue) {
				// only push violation if it’s a meaningful positive comparison
				if (!(limitValue < 0 && totalDue < 0)) {
					newViolations.push({
						rowIndex: rows.map(r => r.rowIndex).join(", "),
						code,
						dueAmt: round2(totalDue),
						limitValue: round2(limitValue),
						advance: null,
						message: "Limit Exceeded",
					});
				}
			}

			// --- Push Advance if valid ---
			if (advance > 0) {
				newViolations.push({
					rowIndex: rows.map(r => r.rowIndex).join(", "),
					code,
					dueAmt: round2(totalDue),
					limitValue: round2(limitValue),
					advance,
					message: "Advance Customer",
				});
			}
		});

		// Step 3: Handle vendors not in file but with opening balance
		customerLimits.forEach((c) => {
			const code = String(c.vendor_code ?? c.subgl ?? "").trim();
			if (!code) return;

			const limitValue = round2(parseNumber(c.from_opening_value));
			if (isNaN(limitValue) || limitValue <= 0) return;

			// If vendor is missing from file → treat full opening value as advance
			if (!fileCodes.has(code)) {
				newViolations.push({
					rowIndex: null,
					code,
					dueAmt: 0,
					limitValue: round2(limitValue),
					advance: round2(limitValue),
					message: "Advance Vendor (Missing in File)",
				});
			}
		});

		// Step 4: Prepare advances array
		let advancesArray = newViolations
			.filter(v => v.advance !== null && v.advance !== undefined && v.advance !== 0)
			.map(v => ({
				code: v.code,
				advance: round2(v.advance),
			}));

		console.log("advancesArrayVendor🔵", advancesArray);

		const advanceList = Array.isArray(AdvanceLimitsVendor)
			? AdvanceLimitsVendor
			: AdvanceLimitsVendor?.data || [];
		console.log("advanceListVendor (normalized)", advanceList);

		const numbersMatch = (a, b) => {
			if (a == null || b == null) return false;
			return Math.abs(round2(parseNumber(a)) - round2(parseNumber(b))) < 0.01;
		};

		advancesArray = advancesArray.filter(a => {
			const match = advanceList.find(api =>
				String(api.vendor_code ?? api.customer_code).trim() === String(a.code).trim() &&
				numbersMatch(api.payment_amt, a.advance)
			);
			return !match;
		});

		// Step 5: Save results
		setViolations(newViolations);
		setAdvancesvendor(advancesArray);

		if (newViolations.length > 0) {
			onOpen();
		}
	}, [customerLimits, AdvanceLimitsVendor, onOpen, setAdvancesvendor]);


	console.log("customerLimitsVendor ", customerLimits);




	function parseNumber(value) {
		if (value === null || value === undefined) return NaN;
		if (typeof value === 'number') return isNaN(value) ? NaN : value;

		let s = String(value).trim();
		if (s === '') return NaN;

		const isParens = /^\(.*\)$/.test(s);
		s = s.replace(/[₹$€£,\s]/g, '');
		s = s.replace(/[^\d.\-()]/g, '');
		if (isParens) {
			s = s.replace(/[()]/g, '');
			s = '-' + s;
		}
		const n = Number(s);
		return isNaN(n) ? NaN : n;
	}

	function getRowValue(row, patterns) {
		const keys = Object.keys(row || {});
		for (const p of patterns) {
			const found = keys.find((k) => k.toLowerCase().replace(/[\s_]/g, '').includes(p));
			if (found) return row[found];
		}
		return undefined;
	}

	function isBlankValue(val) {
		if (val === null || val === undefined) return true;
		if (typeof val === 'number') return false;
		const s = String(val).trim();
		if (s === '') return true;
		const lower = s.toLowerCase();
		if (['na', 'n/a', '-', '--', '—'].includes(lower)) return true;
		return false;
	}

	const demoXlSX = customerList?.message || [];
	function generateCSVData(demoXlSX) {
		if (!demoXlSX || demoXlSX.length === 0) return '';
		const headers = Object.keys(demoXlSX[0]);
		const headerRow = headers.join(',') + '\n';
		const rows = demoXlSX
			.map((item) => {
				const values = headers.map((header) => (item[header] ?? '').toString());
				return values.join(',');
			})
			.join('\n');
		return headerRow + rows;
	}

	const handleDownload = () => {
		if (!demoXlSX || demoXlSX.length === 0) {
			toast({
				title: 'Data is loading, please wait...',
				status: 'info',
				duration: 3000,
				isClosable: true,
			});
			return;
		}

		const updatedData = demoXlSX.map((asset) => ({
			...asset,
			inv_no: '',
			inv_date: '',
			inv_posting_date: '',
			inv_due_date: '',
			inv_total: '',
			due_amt: '',
			inv_sub_total: '',
			cgst: '',
			igst: '',
			sgst: '',
			tds: '',
		}));

		const csvData = generateCSVData(updatedData);
		const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8' });
		saveAs(blob, `Vendor-Invoice-${locationCode || 'LOC'}-${formattedDate}.csv`);
	};

	const handlePageChange = (newPage) => {
		setCurrentPage(newPage);
	};

	return (
		<Box width="100%" height="100%" display="flex" justifyContent="center" alignItems="center" flexDirection="column">
			<Box w="100%" display="flex" justifyContent="space-around">
				<Box width="38%" display="flex" justifyContent="space-around" alignItems="center">
					<Box width="400px">
						<Box border="2px dashed var(--chakra-colors-bgGraycolor)" p="15px" borderRadius="20px" opacity={editing ? '0.5' : '1'} pointerEvents={editing ? 'none' : 'painted'}>
							<Box
								{...getRootProps()}
								bg="var(--chakra-colors-mainBlueColor)"
								width="100%"
								height="100%"
								borderRadius="20px"
								display="flex"
								justifyContent="center"
								alignItems="center"
								color="white"
								padding="50px 0px"
								cursor="grab"
							>
								<Input {...getInputProps()} />
								{isDragActive ? (
									<Box display="flex" flexDirection="column" justifyContent="center" alignItems="center">
										<Image src={uploadIcon} alt="upload icon" height="100px" width="100px" />
										<Text>Drop the Excel file here...</Text>
									</Box>
								) : (
									<Box display="flex" flexDirection="column" justifyContent="center" alignItems="center">
										<Image src={uploadIcon} alt="upload icon" height="100px" width="100px" />
										<Text>Drag & Drop your files</Text>
									</Box>
								)}
							</Box>
						</Box>
						<Box display="flex" width="100%" justifyContent="center" gap="15px" paddingTop="45px">
							<Button borderRadius="10px" border="1px solid #003060" p="20px 30px" width="50%" fontSize="15px" background="none" color="mainBlueColor" onClick={handleDownload} cursor="pointer">
								Download Sample
							</Button>
						</Box>
					</Box>
				</Box>

				<Box width="58%" boxShadow="rgba(0, 0, 0, 0.24) 0px 3px 8px" borderRadius="15px" padding="15px">
					<Heading mb="15px" color="var(--chakra-colors-mainBlueColor)" fontSize="28px">Data Summary :</Heading>
					{loading && (
						<Box display="flex" justifyContent="center" mb="15px">
							<Spinner size="lg" color="blue.500" />
						</Box>
					)}
					<Text fontSize="1.7rem" fontWeight="600" marginBottom="10px" color="green">Total lines : {data?.length}</Text>
					<Text fontSize="1.7rem" fontWeight="600" marginBottom="10px" color="red">Total Error : {errorValue}</Text>
					<Text fontSize="1.7rem" fontWeight="600" color="mainBlueColor" marginBottom="5px">Balance: {bigPrint(totalOpenBalance)}</Text>
					<Box mt="10px" mb="10px">
						<Box fontSize="1.7rem" fontWeight="600" color="mainBlueColor">Pages with errors:</Box>
						{Array.from(pagesWithErrors).join(', ')}
					</Box>
					<Box>
						<Text fontSize="1.7rem" fontWeight="600" color="mainBlueColor" marginBottom="5px">Data Review Report -</Text>
						<AutoTypeText />
					</Box>
				</Box>
			</Box>

			<VendorInvoicePaginatedData
				data={data}
				setData={setData}
				currentPage={currentPage}
				itemsPerPage={itemsPerPage}
				editing={editing}
				setEditing={setEditing}
				onPageChange={handlePageChange}
				setTotalOpenBalance={setTotalOpenBalance}
				setErrorValue={setErrorValue}
				errorValue={errorValue}
				errors={errors}
				setErrors={setErrors}
				hasValidationErrors={hasValidationErrors}
				setHasValidationErrors={setHasValidationErrors}
				setPagesWithErrors={setPagesWithErrors}
				uploadMessageExcel={uploadMessageExcel}
				setUploadMessageExcel={setUploadMessageExcel}
				isParentGlValues={violations.length === 0}
				checkViolations={checkViolations}
				customerLimits={customerLimits}
				setViolations={setViolations}
				onOpen={onOpen}
				onClose={onClose}
			/>


			<Modal
				isOpen={isOpen}
				onClose={() => { setViolations([]); onClose(); }}
				isCentered
				size="xl"
			>
				<ModalOverlay />
				<ModalContent maxW="900px" borderRadius="12px" boxShadow="2xl" bg="white">
					<ModalHeader
						color="red.600"
						m={2}
						fontSize="1.8rem"
						fontWeight="bold"
						textAlign="center"
						borderBottom="2px solid #f5c6cb"
						bg="red.50"
						py={3}
						borderTopRadius="12px"
					>
						⚠ Warning — Issues Found
					</ModalHeader>
					{/* <ModalCloseButton /> */}

					<ModalBody padding="1.5rem" maxH="350px" overflowY="auto">
						<Text mb={5} fontSize="1.2rem" color="gray.700">
							Some rows either exceed the allowed limit or have missing customer codes:
						</Text>

						<VStack spacing={3} align="stretch">
							{violations
								.slice()
								.sort((a, b) => {
									const aNotFound = a.message?.startsWith("Vendor Code not found") ? -1 : 0;
									const bNotFound = b.message?.startsWith("Vendor Code not found") ? -1 : 0;
									return bNotFound - aNotFound;
								})
								.map((v, idx) => (
									<Box
										key={`${v.code}-${idx}`}
										p={3}
										borderRadius="10px"
										border="1px solid"
										borderColor={v.message?.startsWith("Vendor Code not found") ? "red.300" : "orange.300"}
										bg={v.message?.startsWith("Vendor Code not found") ? "red.50" : "orange.50"}
										shadow="sm"
									>
										<Text fontWeight="600" fontSize="1.1rem">
											Row: {v.rowIndex}
										</Text>

										{v.message?.startsWith("Vendor Code not found") ? (
											<Text
												color="red.600"
												fontWeight="bold"
												fontSize="1rem"
												bg="red.100"
												px={3}
												py={1}
												borderRadius="8px"
											>
												{v.message}
											</Text>
										) : (
											<HStack justify="space-between" align="center" spacing={6}>
												<Box flex="1"><Text>Vendor Code: <b>{v.code}</b></Text></Box>
												<Box flex="1"><Text>Due Amount: <b>{v.dueAmt}</b></Text></Box>
												<Box flex="1"><Text>Allowed Limit: <b>{v.limitValue}</b></Text></Box>
												<Box flex="1"><Text>Advance: <b>{v.advance}</b></Text></Box>
											</HStack>
										)}
									</Box>
								))}
						</VStack>
					</ModalBody>

					<Divider />

					<ModalFooter display="flex" justifyContent="space-between" alignItems="center" py="1rem">
						<Text fontSize="1.1rem" fontWeight="bold" color="green.600">
							Total Advances: {violations.filter(v => v.advance && v.advance > 0).length}
						</Text>

						{/* Action Buttons */}
						<HStack spacing={4}>
							<Button
								colorScheme="blue"
								onClick={() => {
									onAdvanceClick();
									setViolations([]);
									onClose();
								}}
								width="200px"
								fontSize="1.2rem"
								fontWeight="bold"
								h="45px"
								borderRadius="8px"
							>
								Add Advance Payment
							</Button>
							<Button
								variant="outline"
								colorScheme="gray"
								onClick={() => {
									setViolations([]);
									onClose();
								}}
								width="130px"
								fontSize="1.2rem"
								fontWeight="bold"
								h="45px"
								borderRadius="8px"
							>
								Cancel
							</Button>
						</HStack>
					</ModalFooter>
				</ModalContent>
			</Modal>

		</Box>
	);
};

export default VendorInvoice;